import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, Inbox, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cap } from "@/lib/format";

export function PageHeader({ title, subtitle, actions, testId }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-testid={testId}>
      <div><h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>{subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}</div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

const STATUS_STYLES = { draft: "bg-slate-100 text-slate-700 border-slate-200", finalized: "bg-emerald-50 text-emerald-700 border-emerald-200", posted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-amber-50 text-amber-700 border-amber-200", void: "bg-red-50 text-red-700 border-red-200", active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  suspended: "bg-red-50 text-red-700 border-red-200", pending: "bg-blue-50 text-blue-700 border-blue-200", accepted: "bg-emerald-50 text-emerald-700 border-emerald-200", revoked: "bg-slate-100 text-slate-600 border-slate-200" };

export const StatusBadge = ({ status, testId }) => (
  <Badge variant="outline" className={`rounded-sm px-1.5 py-0 text-[11px] font-semibold uppercase tracking-wide ${STATUS_STYLES[status] || "bg-slate-100 text-slate-700"}`} data-testid={testId}>{cap(status)}</Badge>
);

export function EmptyState({ title = "Nothing here yet", hint, action, testId = "empty-state" }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-14 text-center" data-testid={testId}>
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600"><Inbox className="h-6 w-6" /></div>
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      {hint && <div className="mt-1 max-w-sm text-xs text-slate-500">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="error-state">
      <div className="font-semibold">Something went wrong</div><div className="mt-1">{message}</div>
      {onRetry && <Button size="sm" variant="outline" className="mt-3" onClick={onRetry} data-testid="retry-button">Retry</Button>}
    </div>
  );
}

export function DataTable({ columns, rows, loading, error, onRetry, meta, page, onPage, sort, onSort, onRowClick, empty, testId = "data-table", rowKey = "id" }) {
  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  const totalPages = meta ? Math.max(1, Math.ceil(meta.total / meta.page_size)) : 1;
  return (
    <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>{columns.map((c) => (
              <th key={c.key} className={`px-4 py-2.5 font-semibold ${c.align === "right" ? "text-right" : "text-left"} ${c.sortable ? "cursor-pointer select-none hover:text-slate-800" : ""}`}
                onClick={() => c.sortable && onSort && onSort(sort === c.key ? `-${c.key}` : c.key)} data-testid={`th-${c.key}`}>
                <span className="inline-flex items-center gap-1">{c.label}{sort === c.key && <ArrowUp className="h-3 w-3" />}{sort === `-${c.key}` && <ArrowDown className="h-3 w-3" />}</span>
              </th>))}</tr>
          </thead>
          <tbody className="divide-y">
            {loading && [...Array(5)].map((_, i) => <tr key={i}>{columns.map((c) => <td key={c.key} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>)}</tr>)}
            {!loading && rows?.length === 0 && <tr><td colSpan={columns.length} className="p-4">{empty || <EmptyState />}</td></tr>}
            {!loading && rows?.map((r, idx) => (
              <tr key={r[rowKey] || idx} className={`transition-colors hover:bg-blue-50/40 ${onRowClick ? "cursor-pointer" : ""}`} onClick={() => onRowClick && onRowClick(r)} data-testid={`row-${r[rowKey] || idx}`}>
                {columns.map((c) => <td key={c.key} className={`px-4 py-2.5 ${c.align === "right" ? "num text-right" : ""} ${c.className || ""}`}>{c.render ? c.render(r) : r[c.key]}</td>)}
              </tr>))}
          </tbody>
        </table>
      </div>
      {meta && meta.total > meta.page_size && (
        <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-slate-500">
          <span data-testid="pagination-info">Showing {(page - 1) * meta.page_size + 1}–{Math.min(page * meta.page_size, meta.total)} of {meta.total}</span>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="num px-2">{page}/{totalPages}</span>
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="pagination-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Toolbar({ search, onSearch, children, onExport, placeholder = "Search…" }) {
  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
      {onSearch && (
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={placeholder} className="pl-8" data-testid="search-input" />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {onExport && <Button variant="outline" size="sm" className="sm:ml-auto" onClick={onExport} data-testid="export-csv-button"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>}
    </div>
  );
}

export function FilterPills({ options, value, onChange, testId = "filter-pills" }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {options.map(([v, label]) => (
        <button key={v} onClick={() => onChange(v)} data-testid={`${testId}-${v || "all"}`}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${value === v ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{label}</button>
      ))}
    </div>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmWord, confirmLabel = "Confirm", destructive, onConfirm, loading, testId = "confirm-dialog" }) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const ok = !confirmWord || typed.trim().toUpperCase() === confirmWord.toUpperCase();
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setTyped(""); setReason(""); } }}>
      <DialogContent className="bg-white sm:max-w-md" data-testid={testId}>
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{description}</DialogDescription></DialogHeader>
        <div className={`rounded-md border p-3 text-xs ${destructive ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid={`${testId}-warning`}>
          This action is irreversible. Finalized financial records cannot be edited; corrections require compensating documents.
        </div>
        {confirmWord && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Type <span className="num font-semibold text-slate-900">{confirmWord}</span> to continue</label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus data-testid={`${testId}-input`} />
          </div>
        )}
        <div className="space-y-1.5"><label className="text-xs font-medium text-slate-600">Reason / note (optional)</label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testId}-reason`} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel-btn`}>Go back</Button>
          <Button variant={destructive ? "destructive" : "default"} disabled={!ok || loading} onClick={() => onConfirm(reason)} data-testid={`${testId}-confirm-btn`}>{loading ? "Working…" : confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const Field = ({ label, children, hint, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}><label className="text-xs font-medium text-slate-600">{label}</label>{children}{hint && <p className="text-[11px] text-slate-400">{hint}</p>}</div>
);

export const Money = ({ value, className = "", testId }) => <span className={`num ${className}`} data-testid={testId}>{new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format((value || 0) / 100)}</span>;
